// Inscrição deste aparelho/navegador para receber push (PWA).
import { api } from "../config/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function sameKey(buffer, expected) {
  if (!buffer) return false;
  const current = new Uint8Array(buffer);
  return current.length === expected.length && current.every((byte, i) => byte === expected[i]);
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermission() {
  return isPushSupported() ? Notification.permission : "unsupported";
}

// iPhone só recebe push com o app instalado na tela de início (iOS 16.4+)
export function isIosWithoutInstall() {
  const ua = window.navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  return isIos && !standalone;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

// Garante que este aparelho está inscrito com a chave atual do servidor e
// avisa o backend. Se a chave do servidor mudou, refaz a inscrição — a antiga
// nunca mais receberia nada. Não pede permissão: chame só com ela concedida.
export async function syncPushSubscription() {
  if (!isPushSupported() || Notification.permission !== "granted") return false;

  const registration = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  if (!data?.publicKey) return false;

  const serverKey = urlBase64ToUint8Array(data.publicKey);
  let subscription = await registration.pushManager.getSubscription();

  if (subscription && !sameKey(subscription.options?.applicationServerKey, serverKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: serverKey,
    });
  }

  const json = subscription.toJSON();

  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });

  return true;
}

// Pede permissão (precisa vir de um clique do usuário) e inscreve o aparelho.
export async function enablePush() {
  if (!isPushSupported()) return "unsupported";

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") return permission;

  await syncPushSubscription();
  return "granted";
}

// Logout: este aparelho para de receber avisos da conta que está saindo.
export async function removePushSubscription() {
  try {
    const subscription = await getCurrentSubscription();
    if (!subscription) return;

    await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
    await subscription.unsubscribe();
  } catch {
    // Sem rede ou sem service worker: o backend limpa sozinho quando falhar
  }
}
